import { DealRequestAllocation } from '../../shared/models';
import { ReactComponent as EquipmentFinancing } from '../../shared/icons/EquipmentFinancing.svg';
import { NumberUtils } from '../../shared/utils';
import { DebtCapitalHeading } from './DebtCapitalHeading';
import {
	CerebroComplexListItem,
	CerebroHeading,
	CerebroLabel,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import { Grid } from '@mui/material';
import NumberFormat from 'react-number-format';

interface DebtEquipmentProps {
	allocation: DealRequestAllocation;
}

export const DebtEquipment: React.FC<DebtEquipmentProps> = ({ allocation }) => {
	const isEmpty = isDebtEquipmentEmpty(allocation);
	const amountRequested = NumberUtils.Sum(
		allocation.purchaseEquipmentAmount,
		allocation.purchaseVehiclesAmount,
		allocation.purchaseMarineAmount,
		allocation.purchaseHelicopterAmount,
		allocation.purchaseFixedWingAircraftAmount
	);

	return (
		<>
			<DebtCapitalHeading icon={EquipmentFinancing} amountRequested={amountRequested}>
				Equipment Financing
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }}>
					<Grid container>
						{allocation.equipmentAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of machinery or equipment?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseEquipmentAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.equipmentAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.vehicleAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of rolling stock or vehicles?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseVehiclesAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.vehicleAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.marineAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of marine vessels?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseMarineAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.marineAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.helicopterAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of helicopters?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseHelicopterAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.helicopterAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.fixedWingAircraftAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of fixed wing aircraft?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseFixedWingAircraftAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.fixedWingAircraftAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						<Grid item xs={12}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Description of equipment being financed.
							</CerebroLabel>
							<CerebroText variant="small">{allocation.equipmentDescription}</CerebroText>
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}
		</>
	);
};

export function isDebtEquipmentEmpty(allocation: DealRequestAllocation): boolean {
	return (
		allocation.equipmentAssetType === 'NotApplicable' &&
		allocation.marineAssetType === 'NotApplicable' &&
		allocation.fixedWingAircraftAssetType === 'NotApplicable' &&
		allocation.helicopterAssetType === 'NotApplicable' &&
		allocation.vehicleAssetType === 'NotApplicable'
	);
}
